import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { observeElementSize } from '../../utils/observeElementSize';
import { createTravelDie } from './portalTravel';
import { createDefeatDieScene } from './defeatDieScene';
import DieMiniature from './DieMiniature';
import { getDie } from './dice';

const DefeatDie = ({ dieId, faceValues, reduceMotion }) => {
  const hostRef = useRef(null);
  const [fallback, setFallback] = useState(false);
  const skin = getDie(dieId);

  useEffect(() => {
    const host = hostRef.current;
    let renderer;
    if (!window.WebGLRenderingContext) {
      setFallback(true);
      return undefined;
    }
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      setFallback(true);
      return undefined;
    }
    setFallback(false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    host.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 20);
    camera.position.set(1.1, 0.85, 2.8);
    camera.lookAt(0, 0, 0);
    scene.add(new THREE.HemisphereLight('#d4faff', '#180a20', 2.4));
    const light = new THREE.DirectionalLight('#fff1dc', 3.5);
    light.position.set(-2, 3, 4);
    scene.add(light);
    const die = createTravelDie(dieId, faceValues);
    const slicedDie = createDefeatDieScene(die, getDie(dieId).color);
    scene.add(slicedDie.group);
    let frame;
    let startedAt;
    let lost = false;
    const render = (now) => {
      if (lost) return;
      if (startedAt === undefined) startedAt = now;
      const elapsed = (now - startedAt) / 1000;
      slicedDie.update(reduceMotion ? 0 : elapsed, reduceMotion);
      renderer.render(scene, camera);
      if (!reduceMotion && elapsed < 3.8) frame = requestAnimationFrame(render);
    };
    const stopObserving = observeElementSize(host, () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      if (reduceMotion) renderer.render(scene, camera);
    });
    const contextLost = (event) => {
      event.preventDefault();
      lost = true;
      renderer.domElement.style.visibility = 'hidden';
      setFallback(true);
    };
    renderer.domElement.addEventListener('webglcontextlost', contextLost);
    frame = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(frame);
      stopObserving();
      renderer.domElement.removeEventListener('webglcontextlost', contextLost);
      const textures = new Set();
      scene.traverse((object) => {
        object.geometry?.dispose();
        const materials = Array.isArray(object.material)
          ? object.material
          : [object.material];
        materials.forEach((material) => {
          if (material?.map) textures.add(material.map);
          material?.dispose();
        });
      });
      textures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.remove();
    };
  }, [dieId, faceValues, reduceMotion]);

  return (
    <div
      className="coreboundDefeatCinematicDie"
      data-die={skin.id}
      style={{ '--die-color': skin.color }}
    >
      <div className="coreboundDefeatDieCanvas" ref={hostRef} />
      {fallback && (
        <div className="coreboundDefeatDieFallback">
          {['whole', 'left', 'right'].map((piece) => (
            <div key={piece} className={`coreboundDefeatDiePiece is-${piece}`}>
              <DieMiniature dieId={dieId} faceValues={faceValues} />
            </div>
          ))}
        </div>
      )}
      <div className="coreboundDefeatDieSlice" />
    </div>
  );
};

export default DefeatDie;
